export const MAX_INLINE_CODE_LINES = 28;
export const MAX_FILE_CODE_LINES = 220;
export const MAX_INLINE_MARKDOWN_CHARS = 4_000;
export const MAX_INLINE_TABLE_ROWS = 12;
export const MAX_FILE_TABLE_ROWS = 120;
export const MAX_TABLE_COLUMNS = 10;
export const MAX_INLINE_DIFF_LINES = 220;
export const MAX_FILE_DIFF_LINES = 900;
export const MAX_JSON_TREE_DEPTH = 6;
export const MAX_JSON_TREE_CHILDREN = 24;
export const MAX_JSON_STRING_LENGTH = 240;

export type AdminToolOutputVariant = 'file' | 'inline';

export type AdminToolOutputKind =
    | 'code'
    | 'console'
    | 'diff'
    | 'json'
    | 'markdown'
    | 'table';

export type AdminToolOutputSelection =
    | {
          readonly kind: 'code';
          readonly code: string;
          readonly language: string | null;
          readonly filename: string | null;
      }
    | {
          // Process-style output (sandbox commands): the
          // human-readable parts are stdout/stderr, exit code, step name,
          // and duration. Everything else stays behind the raw toggle.
          readonly kind: 'console';
          readonly stdout: string;
          readonly stderr: string;
          readonly exitCode: number;
          readonly durationMs: number | null;
          readonly stepName: string | null;
          readonly timedOut: boolean;
          readonly outputTruncated: boolean;
          readonly raw: Record<string, unknown>;
      }
    | {
          readonly kind: 'diff';
          readonly text: string;
          readonly files: ReadonlyArray<ParsedDiffFile>;
      }
    | {
          readonly kind: 'json';
          readonly value: unknown;
      }
    | {
          readonly kind: 'markdown';
          readonly markdown: string;
      }
    | {
          readonly kind: 'table';
          readonly columns: ReadonlyArray<string>;
          readonly rows: ReadonlyArray<ReadonlyArray<string>>;
          readonly summaryLabel: string | null;
      };

export type ParsedDiffLineKind = 'add' | 'context' | 'meta' | 'remove';

export type ParsedDiffLine = {
    readonly kind: ParsedDiffLineKind;
    readonly text: string;
};

export type ParsedDiffHunk = {
    readonly header: string;
    readonly lines: ReadonlyArray<ParsedDiffLine>;
};

export type ParsedDiffFile = {
    readonly label: string;
    readonly language: string | null;
    readonly hunks: ReadonlyArray<ParsedDiffHunk>;
};

export type AdminToolOutputRenderSource = {
    readonly output: unknown;
    readonly text?: string | null;
    readonly filename?: string | null;
};

const CODE_FENCE_PATTERN = /^```([^\n`]*)\n?([\s\S]*?)\n?```$/;

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeKindHint(value: unknown): AdminToolOutputKind | null {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    switch (normalized) {
        case 'code':
        case 'diff':
        case 'json':
        case 'markdown':
        case 'table':
            return normalized;
        case 'patch':
            return 'diff';
        default:
            return null;
    }
}

export function toDisplayString(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
}

export function getStringField(
    record: Record<string, unknown>,
    ...keys: ReadonlyArray<string>
): string | null {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === 'string' && value.trim().length > 0) {
            return value;
        }
    }
    return null;
}

export function getKindHint(
    record: Record<string, unknown>,
): AdminToolOutputKind | null {
    return normalizeKindHint(
        record['content_kind'] ??
            record['contentKind'] ??
            record['output_kind'] ??
            record['outputKind'] ??
            record['kind'],
    );
}

export function getLanguageHint(
    record: Record<string, unknown>,
): string | null {
    return getStringField(
        record,
        'language',
        'language_hint',
        'languageHint',
        'lexer',
    );
}

export function getFilenameHint(
    record: Record<string, unknown>,
    fallbackFilename: string | null,
): string | null {
    return (
        getStringField(record, 'filename', 'file_name', 'path', 'file_path') ??
        fallbackFilename
    );
}

export function parseJsonString(value: string): unknown | null {
    const trimmed = value.trim();
    if (
        trimmed.length === 0 ||
        (!trimmed.startsWith('{') && !trimmed.startsWith('['))
    ) {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

export function parseCodeFence(value: string): {
    readonly code: string;
    readonly language: string | null;
} | null {
    const match = value.trim().match(CODE_FENCE_PATTERN);
    if (!match) {
        return null;
    }
    const language = match[1]?.trim().toLowerCase() || null;
    return {
        code: match[2] ?? '',
        language,
    };
}

export function isDiffLikeText(
    text: string,
    languageHint: string | null,
): boolean {
    const normalizedLanguage = languageHint?.toLowerCase() ?? null;
    if (normalizedLanguage === 'diff' || normalizedLanguage === 'patch') {
        return true;
    }
    const lines = text.split('\n');
    let signalCount = 0;
    if (text.startsWith('diff --git ')) {
        signalCount += 2;
    }
    if (lines.some((line) => line.startsWith('@@ '))) {
        signalCount += 1;
    }
    if (
        lines.some(
            (line) =>
                line.startsWith('--- ') ||
                line.startsWith('+++ ') ||
                line.startsWith('index '),
        )
    ) {
        signalCount += 1;
    }
    const patchLineCount = lines.filter(
        (line) =>
            line.startsWith('+') ||
            line.startsWith('-') ||
            line.startsWith(' '),
    ).length;
    if (patchLineCount >= 4) {
        signalCount += 1;
    }
    return signalCount >= 2;
}

export function looksLikeCode(
    text: string,
    languageHint: string | null,
): boolean {
    if (languageHint && languageHint.toLowerCase() !== 'markdown') {
        return true;
    }
    const trimmed = text.trim();
    if (!trimmed.includes('\n')) {
        return false;
    }
    const indicators = [
        /\bfunction\b/,
        /\breturn\b/,
        /\bconst\b/,
        /\blet\b/,
        /\bdef\b/,
        /\bclass\b/,
        /\bimport\b/,
        /\bfrom\b/,
        /\bSELECT\b/i,
        /\bUPDATE\b/i,
        /^[ \t]*[#/]{1,2}.+/m,
        /[{[();]}]/,
    ];
    let signalCount = 0;
    for (const indicator of indicators) {
        if (indicator.test(trimmed)) {
            signalCount += 1;
        }
    }
    return signalCount >= 3;
}

export function normalizeLanguage(value: string | null): string | null {
    if (!value) {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    switch (normalized) {
        case 'javascript':
        case 'js':
            return 'javascript';
        case 'typescript':
        case 'ts':
            return 'typescript';
        case 'tsx':
            return 'tsx';
        case 'jsx':
            return 'jsx';
        case 'python':
        case 'py':
            return 'python';
        case 'shell':
        case 'bash':
        case 'sh':
            return 'bash';
        case 'sql':
            return 'sql';
        case 'json':
            return 'json';
        case 'markdown':
        case 'md':
            return 'markdown';
        case 'diff':
        case 'patch':
            return 'diff';
        default:
            return normalized;
    }
}

export function inferLanguageFromFilename(
    filename: string | null,
): string | null {
    if (!filename) {
        return null;
    }
    const normalized = filename.toLowerCase();
    if (normalized.endsWith('.tsx')) {
        return 'tsx';
    }
    if (normalized.endsWith('.ts')) {
        return 'typescript';
    }
    if (normalized.endsWith('.jsx')) {
        return 'jsx';
    }
    if (normalized.endsWith('.js')) {
        return 'javascript';
    }
    if (normalized.endsWith('.py')) {
        return 'python';
    }
    if (normalized.endsWith('.sql')) {
        return 'sql';
    }
    if (normalized.endsWith('.json')) {
        return 'json';
    }
    if (normalized.endsWith('.sh')) {
        return 'bash';
    }
    if (normalized.endsWith('.md')) {
        return 'markdown';
    }
    return null;
}

export function inferLanguageFromCode(text: string): string | null {
    if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
        return 'json';
    }
    if (/\b(def|from|import|class|async def|None|True|False)\b/.test(text)) {
        return 'python';
    }
    if (
        /\b(const|let|function|export|import|interface|type|await)\b/.test(text)
    ) {
        return 'typescript';
    }
    if (/\b(SELECT|UPDATE|INSERT|DELETE|FROM|WHERE|JOIN)\b/i.test(text)) {
        return 'sql';
    }
    if (/^\s*#!/.test(text) || /\b(echo|export|fi|done)\b/.test(text)) {
        return 'bash';
    }
    return null;
}

export function inferLanguage({
    explicitLanguage,
    filename,
    text,
}: {
    readonly explicitLanguage: string | null;
    readonly filename: string | null;
    readonly text: string;
}): string | null {
    return (
        normalizeLanguage(explicitLanguage) ??
        inferLanguageFromFilename(filename) ??
        inferLanguageFromCode(text)
    );
}

export function truncateLines({
    text,
    maxLines,
}: {
    readonly text: string;
    readonly maxLines: number;
}): {
    readonly hiddenLineCount: number;
    readonly visibleLines: ReadonlyArray<string>;
} {
    const lines = text.split('\n');
    return {
        hiddenLineCount: Math.max(lines.length - maxLines, 0),
        visibleLines: lines.slice(0, maxLines),
    };
}

export function formatLanguageLabel(language: string | null): string | null {
    if (!language) {
        return null;
    }
    switch (language) {
        case 'tsx':
            return 'TSX';
        case 'jsx':
            return 'JSX';
        case 'typescript':
            return 'TypeScript';
        case 'javascript':
            return 'JavaScript';
        case 'python':
            return 'Python';
        case 'json':
            return 'JSON';
        case 'sql':
            return 'SQL';
        case 'bash':
            return 'Bash';
        case 'diff':
            return 'Diff';
        case 'markdown':
            return 'Markdown';
        default:
            return language;
    }
}
